<template>
  <section
    class="accruals-estimate-gl-card"
    :data-testid="testid"
  >
    <div class="accruals-estimate-gl-card__step">
      <span class="accruals-estimate-gl-card__badge" aria-hidden="true">
        <img
          :src="assets.ESTIMATES_STEP_CHECK"
          alt=""
          class="accruals-estimate-gl-card__check"
        />
      </span>
      <span class="accruals-estimate-gl-card__confirmed">
        {{ isRecorded ? copy.RECORDED_PREFIX : copy.CONFIRMED_PREFIX }}
        · {{ amountLabel }}
      </span>
      <template v-if="!isRecorded">
        <span class="accruals-estimate-gl-card__arrow" aria-hidden="true">→</span>
        <span class="accruals-estimate-gl-card__next">{{ copy.NEXT_STEP }}</span>
      </template>
    </div>

    <div
      v-if="amountSourceLabel"
      class="accruals-estimate-gl-card__source"
    >
      Confirmed via {{ amountSourceLabel }}
    </div>

    <div class="accruals-estimate-gl-card__fields">
      <div class="accruals-estimate-gl-card__field">
        <label class="accruals-estimate-gl-card__label">{{ copy.DEBIT_GL }}</label>
        <p
          v-if="isRecorded"
          class="accruals-estimate-gl-card__value"
          data-testid="accruals-estimate-gl-card__debit-value"
        >
          {{ formatGl(debitGl) }}
        </p>
        <lb-dropdown
          v-else
          :value="debitGl"
          :items="glDropdownItems"
          :hidedetails="!debitError"
          :error="debitError"
          label=""
          searchable
          :placeholder="copy.PLACEHOLDER"
          class="accruals-estimate-gl-card__input"
          data-testid="accruals-estimate-gl-card__debit-gl"
          @input="$emit('update:debitGl', $event)"
        />
      </div>
      <div class="accruals-estimate-gl-card__field">
        <label class="accruals-estimate-gl-card__label">{{ copy.CREDIT_GL }}</label>
        <p
          v-if="isRecorded"
          class="accruals-estimate-gl-card__value"
          data-testid="accruals-estimate-gl-card__credit-value"
        >
          {{ formatGl(creditGl) }}
        </p>
        <lb-dropdown
          v-else
          :value="creditGl"
          :items="glDropdownItems"
          :hidedetails="!creditError"
          :error="creditError"
          label=""
          searchable
          :placeholder="copy.PLACEHOLDER"
          class="accruals-estimate-gl-card__input"
          data-testid="accruals-estimate-gl-card__credit-gl"
          @input="$emit('update:creditGl', $event)"
        />
      </div>
    </div>

    <v-btn
      v-if="isRecorded && canUnlock"
      class="accruals-estimate-gl-card__action primaryBtn"
      block
      data-testid="accruals-estimate-gl-card__unlock-btn"
      @click="$emit('unlock')"
    >
      Unlock estimate
    </v-btn>
    <v-btn
      v-else-if="!isRecorded && canReview"
      class="accruals-estimate-gl-card__action primaryBtn"
      block
      :loading="saving"
      :disabled="saving"
      data-testid="accruals-estimate-gl-card__record-btn"
      @click="onRecord"
    >
      {{ copy.RECORD_ENTRY }}
    </v-btn>
  </section>
</template>

<script>
import { ACCRUALS_ASSETS } from '../accrualsPageConstants';
import {
  ACCRUALS_ESTIMATE_SOURCE_LABELS,
  ACCRUALS_GL_ASSIGNMENT_COPY,
  formatEstimateMoney,
  formatGlOptionLabel,
} from '../accrualsEstimatesConstants';

export default {
  name: 'AccrualsEstimateGlCard',
  props: {
    mode: {
      type: String,
      default: 'assign',
    },
    amount: {
      type: [Number, String],
      default: null,
    },
    amountSource: {
      type: String,
      default: '',
    },
    debitGl: {
      type: String,
      default: '',
    },
    creditGl: {
      type: String,
      default: '',
    },
    canReview: {
      type: Boolean,
      default: false,
    },
    canUnlock: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    testid: {
      type: String,
      default: 'accruals-estimate-gl-card',
    },
  },
  data() {
    return {
      assets: ACCRUALS_ASSETS,
      copy: {
        ...ACCRUALS_GL_ASSIGNMENT_COPY,
        RECORDED_PREFIX: 'Estimate recorded',
      },
      showFieldErrors: false,
    };
  },
  computed: {
    isRecorded() {
      return this.mode === 'recorded';
    },
    storeGlOptions() {
      return this.$store.state.accruals?.glOptions
        || this.$store.state.m_financialclose?.accruals?.glOptions
        || [];
    },
    glDropdownItems() {
      const options = this.storeGlOptions.map((row) => formatGlOptionLabel(row));
      [this.debitGl, this.creditGl].forEach((value) => {
        const label = formatGlOptionLabel(value);
        if (label && !options.includes(label)) options.push(label);
      });
      return options;
    },
    amountLabel() {
      return formatEstimateMoney(this.amount);
    },
    amountSourceLabel() {
      return ACCRUALS_ESTIMATE_SOURCE_LABELS[this.amountSource] || '';
    },
    canSave() {
      return Boolean(this.debitGl) && Boolean(this.creditGl);
    },
    debitError() {
      if (!this.showFieldErrors || this.debitGl) return '';
      return this.copy.FIELD_REQUIRED;
    },
    creditError() {
      if (!this.showFieldErrors || this.creditGl) return '';
      return this.copy.FIELD_REQUIRED;
    },
  },
  created() {
    this.$store.dispatch('accruals/loadGlOptions');
  },
  beforeDestroy() {
    this.showFieldErrors = false;
  },
  methods: {
    formatGl(value) {
      return formatGlOptionLabel(value) || '—';
    },
    onRecord() {
      if (!this.canReview || this.saving) return;
      if (!this.canSave) {
        this.showFieldErrors = true;
        this.$store.commit('sbError', this.copy.RECORD_ENTRY_INVALID, { root: true });
        console.log('[AccrualsEstimateGlCard]:', { action: 'record-entry-invalid' });
        return;
      }
      this.showFieldErrors = false;
      console.log('[AccrualsEstimateGlCard]:', {
        action: 'record-entry',
        debitGl: this.debitGl,
        creditGl: this.creditGl,
      });
      this.$emit('save', {
        debitGl: this.debitGl,
        creditGl: this.creditGl,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimateGlCard.scss';
</style>
